import DiseaseEncyclopediaModel from '@/entities/disease-encyclopedia/model/diseaseEncyclopedia';
import { createDiseaseEncyclopediaDetailUrl } from '@/entities/disease-encyclopedia/util';
import useGetKeywordIntegratedSearchDiseaseEncyclopedia, {
  EKeywordSearchDiseaseEncyclopediaSortType,
} from '@/hooks/search/useGetKeywordIntegratedSearchDiseaseEncyclopedia';
import useCustomRouter from '@/hooks/useCustomRouter';
import { BreadCrumb, ROUTES } from '@/types/common';
import { SearchResult } from '@/types/search';
import { addQueryString } from '@/utils/url';
import { useMemo, useRef, useState } from 'react';

export interface DiseaseEncyclopediaTotalViewModel {
  keywordSearchDiseaseEncyclopedia?: SearchResult<DiseaseEncyclopediaModel>;
  keyword: string;
  searchInputRef: React.RefObject<HTMLInputElement>;
  currentPage: number;
  queries: { page: string };
  totalPage: number;
  totalCount: number;
  handleSubmit: () => void;
  handleResetSearchKeyword: () => void;
  handleBackButtonClick: () => void;
  onClickDiseaseEncyclopedia: (diseaseEncyclopedia: DiseaseEncyclopediaModel) => () => void;
  handleChangeSearchKeyword: (e: React.ChangeEvent<HTMLInputElement>) => void;
  breadCrumbs: BreadCrumb[];
}

const useDiseaseEncyclopediaTotalViewModel = (): DiseaseEncyclopediaTotalViewModel => {
  const { router } = useCustomRouter();
  const queries = router.query as { page: string };

  const breadCrumbs = useMemo(() => {
    return [
      {
        label: '질환 백과',
        url: `${ROUTES.DISEASE_ENCYCLOPEDIA.HOME.path}`,
        logName: 'disease_encyclopedia_home',
      },
      {
        label: '전체 질환',
        url: `${ROUTES.DISEASE_ENCYCLOPEDIA.TOTAL.path}${addQueryString(queries)}`,
        logName: 'disease_encyclopedia_total',
      },
    ];
  }, [queries]);

  const currentPage = Number(queries.page || '1');
  const [keyword, setKeyword] = useState('');

  const searchInputRef = useRef<HTMLInputElement>(null);

  const { keywordSearchDiseaseEncyclopedia, totalPage, totalCount } = useGetKeywordIntegratedSearchDiseaseEncyclopedia({
    keyword: undefined,
    enabled: true,
    limit: 10,
    page: currentPage,
    sortType: EKeywordSearchDiseaseEncyclopediaSortType.Name,
  });

  const handleSubmit = () => {
    const searchKeyword = keyword.trim();
    if (searchKeyword) {
      router.replace({
        pathname: ROUTES.DISEASE_ENCYCLOPEDIA.SEARCH.path,
        query: { keyword: searchKeyword },
      });
    }
  };

  const handleResetSearchKeyword = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    router.push({
      pathname: ROUTES.DISEASE_ENCYCLOPEDIA.HOME.path,
      query: {},
    });
  };

  const handleBackButtonClick = () => {
    router.back();
  };

  const onClickDiseaseEncyclopedia = (diseaseEncyclopedia: DiseaseEncyclopediaModel) => () => {
    router.push(createDiseaseEncyclopediaDetailUrl(diseaseEncyclopedia));
  };

  const handleChangeSearchKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(e.target.value);
  };

  return {
    keyword,
    keywordSearchDiseaseEncyclopedia,
    searchInputRef,
    currentPage,
    queries,
    totalPage,
    totalCount,
    handleSubmit,
    handleResetSearchKeyword,
    handleBackButtonClick,
    onClickDiseaseEncyclopedia,
    handleChangeSearchKeyword,
    breadCrumbs,
  };
};

export default useDiseaseEncyclopediaTotalViewModel;
